import axios from "axios";
import globalCalls from "../modules/GlobalCalls";
import ArtistSuggestionsStatusService from "./ArtistSuggestionsStatusService";
import {user} from "../modules/User";

// Базовый URL для API
const BASE_URL = process.env.REACT_APP_API_URL;

// глобальные подкрутки для всех запросов по умолчанию (могут быть переопределены в конкретных запросах)
// имитация долгого ответа от сервера (0 = выключена)
const SIMULATE_RESPONSE_DELAY = 0;
// имитация возврата ошибки при запросе (задает текст ошибки. если пусто = имитация выключена)
const SIMULATE_RESPONSE_ERROR = "";

export default class ServerManager {
    static rqCounter = 0;

    static async makeRequest(method, endpoint, dataOrParams = undefined,
                             simulateResponseDelay = undefined, simulateResponseError = undefined,
                             throwErrorDetails = false){

        return this.makeRequestWithUrl(method, `${BASE_URL}/${endpoint}`, dataOrParams, simulateResponseDelay,
            simulateResponseError, throwErrorDetails);
    }

    // NOTE: если заданы (не undefined) simulateResponseDelay или simulateResponseError, то они
    //   переопределяют соответствующие глобальные настройки для данного запроса
    static async makeRequestWithUrl(method, url, dataOrParams = undefined,
                                    simulateResponseDelay = undefined, simulateResponseError = undefined,
                                    throwErrorDetails = false){

        // TODO: setup once on start?
        axios.defaults.withCredentials = true;

        if (method !== "get" && method !== "post" && method !== "put" && method !== "delete")
            this.throwError(`invalid request method: ${method}`, throwErrorDetails);

        const rqId = ++this.rqCounter;
        const prefix = `srv rq #${rqId}:`;

        try {
            if (dataOrParams)
                console.log(`${prefix} send(${method}), url:`, url, (method === "post" || method === "put" || method === "delete" ? "data:" : "params:"), dataOrParams);
            else
                console.log(`${prefix} send(${method}), url:`, url);

            // (опционально) имитация долгого ответа (просто задержка на заданное кол-во миллисекунд)
            // если был задан параметр simulateResponseDelay, то используется он, иначе используется глобальная константа
            const simDelay = simulateResponseDelay !== undefined ? simulateResponseDelay : SIMULATE_RESPONSE_DELAY;
            if (simDelay > 0)
                await new Promise(resolve => setTimeout(resolve, simDelay));

            const response = method === "post" ? await axios.post(url, dataOrParams) :
                method === "put" ? await axios.put(url, dataOrParams) :
                    method === "delete" ? await axios.delete(url, dataOrParams ? { params: dataOrParams } : undefined) :
                await axios.get(url, dataOrParams ? { params: dataOrParams } : undefined);

            // (опционально) имитация ответа с ошибкой (игнорируем реальный ответ и кидаем ошибку)
            // если был задан параметр simulateResponseError, то используется он, иначе используется глобальная константа
            const simError = simulateResponseError !== undefined ? simulateResponseError : SIMULATE_RESPONSE_ERROR;
            if (simError)
                throw simError;

            console.log(`${prefix} done, data:`, response.data.data);
            if (response.data.data)
                response.data.data.refs = response.data.refs; // HACK! TODO??
            return response.data.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response) {
                    console.error(`${prefix} error (httpStatus=${error.response.status}), data:`, error.response.data);
                    if (error.response.status === 401) {
                        globalCalls.logout();
                        this.throwError("Необходимо выполнить вход для авторизации", throwErrorDetails);
                    }
                    else if (error.response.status >= 400 && error.response.status < 600) {
                        const parts = [];
                        const errorData = error.response.data;

                        if (errorData?.display_message)
                            parts.push(errorData.display_message);
                        if (errorData?.error)
                            parts.push(errorData.error);
                        if (parts.length === 0)
                            parts.push("error");
                        if (errorData?.error_description)
                            parts.push(errorData.error_description);
                        if (errorData?.error_data)
                            parts.push("data=" + JSON.stringify(errorData.error_data));

                        throw parts.join("\n\r");
                    }
                    else if (error.response.status === 400 && error.response.data?.error) {
                        let msg = error.response.data.error;
                        if (error.response.data.error_description)
                            msg += "; " + error.response.data.error_description;
                        if (error.response.data.error_data)
                            msg += "; data=" + JSON.stringify(error.response.data.error_data);
                        this.throwError(msg, throwErrorDetails, error.response);
                    }
                } else if (error.request) {
                    console.error(`${prefix} network error (${error.message}), request:`, error.request);
                } else {
                    console.error(`${prefix} error:`, error.message);
                }

                this.throwError(error.message, throwErrorDetails);
            }

            console.error(`${prefix} error:`, error.message ?? error);
            this.throwError(error.message ?? "error", throwErrorDetails, error);
        }
    }

    static throwError(msg, throwAsObject, data) {
        if (throwAsObject)
            throw { msg: msg, data: data };
        else
            throw msg;
    }

    static async login(email, password) {
        return this.makeRequest("post", "login", { email: email, password: password });
    }

    static async logout() {
        return this.makeRequest("post", "logout");
    }

    static async checkAuth() {
        return this.makeRequest("get", "current");
    }

    static async getSimpleModeFilteredList(modeApiName, limit, query, sortType, sortDir, offset = 0, extraParams = {}) {
        // temp hack!
        if (modeApiName === "event-drafts/locations") {
            const tempData = await this.makeRequest("get", `${modeApiName}`, { ...extraParams, limit: limit, query: query.trim(), offset: offset, sort_by: sortType, sort_dir: sortDir });

            const newData = {}
            newData["event-drafts/locations"] = tempData;
            newData.pagination = { is_last: true }

            return newData;
        }


        const data = await this.makeRequest("get", `${modeApiName}`, { ...extraParams, limit: limit, query: query.trim(), offset: offset, sort_by: sortType, sort_dir: sortDir });

        if (modeApiName === "artist_suggestions") {
            ArtistSuggestionsStatusService.forceSetCurrentTotal(data.total);
        }

        return data;
    }

    static async getSimpleModeItem(modeApiName, id) {
        return await this.makeRequest("get", `${modeApiName}/${id}`);
    }

    static async addSimpleModeItem(modeApiName, data) {
        const result = await this.makeRequest("post", `${modeApiName}`, data);

        if (modeApiName === "event") {
            user.addedEventCount++;
        }

        return result;
    }

    static async saveSimpleModeItem(modeApiName, id, data) {
        return this.makeRequest("put", `${modeApiName}/${id}`, data);
    }

    static async deleteArtistSuggestion(id) {
        return this.makeRequest("delete", `artist_suggestion/${id}`);
    }

    static async resolveArtistSuggestion(id, artistId) {
        return this.makeRequest("post", `artist_suggestion/${id}/resolve`, { artist_id: artistId });
    }

    static async getWeightCoefficients() {
        return await this.makeRequest("get", "weight-coefficients"); // TODO: _
    }

    static async saveWeightCoefficients(data) {
        return await this.makeRequest("put", "weight-coefficients", data); // TODO: _
    }

    static async getSurvey() {
        return await this.makeRequest("get", "survey");
    }

    static async getSurveyFilteredList(limit, offset = 0, extraParams = {}) {
        return await this.makeRequest("get", "survey/responses", { ...extraParams, limit: limit, offset: offset });
    }

    static async getSurveyAggregation(extraParams = {}) {
        return await this.makeRequest("get", "survey/aggregation", { ...extraParams });
    }

    static async getSubscriptionCancellationsAggregation(extraParams = {}) {
        return await this.makeRequest("get", "subscription-cancellations/aggregation", { ...extraParams });
    }

    static async getSubscriptionCancellationsFilteredList(limit, offset = 0, extraParams = {}) {
        return await this.makeRequest("get", "subscription-cancellations/custom", { ...extraParams, limit: limit, offset: offset });
    }

    static async exportLocationEvents(locationId) {
        try {
            axios.defaults.withCredentials = true; // TODO?
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/location/${locationId}/events/export`, {
                responseType: 'blob', // ключевой момент — иначе axios попытается распарсить как JSON/текст
                // headers: { Authorization: `Bearer ${token}` }, // если нужна авторизация
            });

            // Имя файла из Content-Disposition
            /*let filename = 'downloaded-file';
            const disposition = response.headers['content-disposition'];
            if (disposition) {
                const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/);
                if (match && match[1]) {
                    filename = decodeURIComponent(match[1]);
                }
            }*/
            const filename = `events_location_${locationId}.xlsx`;

            // response.data уже является Blob благодаря responseType: 'blob'
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            //console.error('Ошибка при скачивании файла:', error);
            throw error;
        }
    }

    static async importLocationEvents(locationId, base64) {
        try {
            return await this.makeRequest("post", `location/${locationId}/events/import`, { file_base64: base64 }, undefined, undefined, true);
        }
        catch (error) {
            if (error?.data?.status === 400 && error.data.data?.error_data?.file_base64 === "location_mismatch") {
                throw "Файл не связан с данной локацией (использован файл от другой локации?)";
            }

            throw error.msg ?? error;
        }
    }

    static async deleteEventDraft(id) {
        return this.makeRequest("delete", `event-draft/${id}`);
    }
}